import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Download,
  FileCheck,
  FileX,
  RefreshCw,
  Send,
  ShieldCheck,
} from "lucide-react";
import { ApiError } from "@/lib/api";
import {
  useApproveCertRequest,
  useCertRequest,
  useFinalizeCertRequest,
  useMarkReady,
  useRejectCertRequest,
  useRunTestsetid,
  useSyncCertRequest,
  useSyncTenant,
} from "@/lib/api/cert-requests.queries";
import type {
  CertRequestAuditEntry,
  CertRequestDetail,
  CertRequestDocument,
  CertRequestDocType,
  CertRequestStatus,
  FinalizeCertRequestBody,
  RunTestsetIdBody,
} from "@/lib/api/cert-requests";
import { downloadCertRequestDocument } from "@/lib/api/cert-requests";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import { CenteredSpinner } from "@/components/ui/Spinner";
import { Dialog } from "@/components/ui/Dialog";
import { EmptyState } from "@/components/ui/EmptyState";
import { ErrorState } from "@/components/ui/ErrorState";
import { Input, Label } from "@/components/ui/Input";
import { formatDate } from "@/lib/utils";
import { cn } from "@/lib/utils";
import {
  emptyFinalizeCertRequestBody,
  normalizeFinalizeCertRequestBody,
} from "@/lib/finalize-cert-request";

// ─── Status palettes ─────────────────────────────────────────────────────────

const ONBOARDING_TONE: Record<
  string,
  "neutral" | "warning" | "info" | "success" | "danger" | "brand"
> = {
  pending: "neutral",
  cert_request_pending: "warning",
  cert_request_submitted: "warning",
  cert_request_rejected: "danger",
  cert_uploaded: "info",
  hab_passed: "info",
  prod_synced: "brand",
  ready: "success",
};

const CERT_REQ_TONE: Record<
  CertRequestStatus,
  "neutral" | "warning" | "info" | "success" | "danger" | "brand"
> = {
  draft: "neutral",
  submitted: "warning",
  verified: "info",
  issued: "brand",
  rejected: "danger",
  revoked: "danger",
};

const CERT_REQ_LABEL: Record<CertRequestStatus, string> = {
  draft: "Borrador",
  submitted: "Enviada",
  verified: "Verificada",
  issued: "Emitida",
  rejected: "Rechazada",
  revoked: "Revocada",
};

const DOC_LABEL: Record<CertRequestDocType, string> = {
  rut: "RUT",
  camara_comercio: "Cámara de Comercio",
  cedula: "Cédula del representante",
  poder: "Poder",
  formulario: "Formulario",
};

const DOC_ORDER: CertRequestDocType[] = [
  "rut",
  "camara_comercio",
  "cedula",
  "poder",
  "formulario",
];

const ACTION_LABEL: Record<string, string> = {
  approve: "Aprobada",
  reject: "Rechazada",
  sync: "Sincronización",
  finalize: "Finalización",
  mark_ready: "Marcada lista",
  run_testsetid: "Run testset",
  sync_tenant: "Sync tenant",
};

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isDetail(d: unknown): d is CertRequestDetail {
  return (
    !!d && typeof d === "object" && "company" in (d as Record<string, unknown>)
  );
}

function errorToText(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return `[${err.code}] ${err.message}`;
  if (err instanceof Error) return err.message;
  return fallback;
}

function safeDownloadFileName(raw: string, documentId: string): string {
  const safe = Array.from(raw, (char) =>
    char.charCodeAt(0) < 32 ||
    char.charCodeAt(0) === 127 ||
    char === "/" ||
    char === "\\"
      ? "_"
      : char,
  )
    .join("")
    .trim();
  return safe || `documento-${documentId}`;
}

// ─── Panel ───────────────────────────────────────────────────────────────────

export function CertRequestPanel({ companyId }: { companyId: string }) {
  const query = useCertRequest(companyId);

  if (query.isPending) return <CenteredSpinner label="Cargando solicitud…" />;
  if (query.isError)
    return <ErrorState error={query.error} onRetry={() => query.refetch()} />;

  const data = query.data;
  if (!isDetail(data) || !data.certRequest) {
    return (
      <Card>
        <CardContent>
          <EmptyState
            icon={FileX}
            title="Sin solicitud de certificado"
            description="Esta empresa todavía no ha iniciado el flujo de cert-request desde la app del cliente."
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <PanelHeader detail={data} />
        <DocumentsGrid detail={data} />
        <ActionBar detail={data} companyId={companyId} />
      </div>
      <div className="lg:col-span-1">
        <AuditTimeline entries={data.audit} />
      </div>
    </div>
  );
}

// ─── Header ──────────────────────────────────────────────────────────────────

function PanelHeader({ detail }: { detail: CertRequestDetail }) {
  const { company, certRequest } = detail;
  const onbTone = ONBOARDING_TONE[company.onboardingStatus] ?? "neutral";
  return (
    <Card>
      <CardContent className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-sm text-slate-500">Empresa</p>
          <p className="text-lg font-semibold text-slate-900">
            {company.legalName}
          </p>
          <p className="text-xs font-mono text-slate-500 mt-0.5">
            NIT {company.nit}
            {company.dv ? `-${company.dv}` : ""} · ambiente DIAN{" "}
            <span className="uppercase">{company.dianEnv}</span>
          </p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex flex-col items-end gap-1">
            <span className="text-[11px] uppercase tracking-wide text-slate-500">
              Onboarding
            </span>
            <Badge tone={onbTone} className="font-mono">
              {company.onboardingStatus}
            </Badge>
          </div>
          {certRequest && (
            <div className="flex flex-col items-end gap-1">
              <span className="text-[11px] uppercase tracking-wide text-slate-500">
                Solicitud
              </span>
              <Badge tone={CERT_REQ_TONE[certRequest.status] ?? "neutral"}>
                {CERT_REQ_LABEL[certRequest.status] ?? certRequest.status}
              </Badge>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Documents ───────────────────────────────────────────────────────────────

function DocumentsGrid({ detail }: { detail: CertRequestDetail }) {
  const byType = useMemo(() => {
    const m = new Map<string, CertRequestDetail["documents"][number]>();
    for (const d of detail.documents) m.set(d.docType, d);
    return m;
  }, [detail.documents]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Documentos cargados</CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
        {DOC_ORDER.map((dt) => {
          const doc = byType.get(dt);
          return (
            <div
              key={dt}
              className={cn(
                "border rounded-md p-3 text-xs space-y-1",
                doc
                  ? "border-slate-200 bg-white"
                  : "border-dashed border-slate-200 bg-slate-50",
              )}
            >
              <div className="flex items-center gap-1.5">
                <FileCheck
                  className={cn(
                    "h-3.5 w-3.5",
                    doc ? "text-emerald-600" : "text-slate-300",
                  )}
                />
                <span className="font-medium text-slate-700">
                  {DOC_LABEL[dt]}
                </span>
              </div>
              {doc ? (
                <>
                  <p
                    className="font-mono text-[11px] text-slate-700 truncate"
                    title={doc.fileName}
                  >
                    {doc.fileName}
                  </p>
                  <p className="text-[11px] text-slate-500">
                    {formatBytes(doc.sizeBytes)} · subido{" "}
                    {formatDate(doc.uploadedAt)}
                  </p>
                  <DocumentDownloadButton
                    companyId={detail.company.id}
                    certRequestId={detail.certRequest!.id}
                    certDocument={doc}
                  />
                </>
              ) : (
                <p className="text-[11px] text-slate-400">Pendiente</p>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

function DocumentDownloadButton({
  companyId,
  certRequestId,
  certDocument,
}: {
  companyId: string;
  certRequestId: string;
  certDocument: CertRequestDocument;
}) {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onDownload() {
    setDownloading(true);
    setError(null);
    try {
      const result = await downloadCertRequestDocument(
        companyId,
        certRequestId,
        certDocument.id,
      );
      const objectUrl = URL.createObjectURL(result.blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = safeDownloadFileName(
        result.fileName || certDocument.fileName,
        certDocument.id,
      );
      anchor.rel = "noopener";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1_000);
    } catch (err) {
      setError(errorToText(err, "No pudimos descargar el documento."));
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="space-y-1">
      <button
        type="button"
        onClick={onDownload}
        disabled={downloading}
        className="inline-flex items-center gap-1 text-brand-700 hover:text-brand-800 text-[11px] font-medium disabled:cursor-wait disabled:text-slate-400"
      >
        <Download className={cn("h-3 w-3", downloading && "animate-pulse")} />
        {downloading ? "Descargando…" : "Descargar"}
      </button>
      {error && (
        <p role="alert" className="text-[11px] leading-tight text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}

// ─── Action bar ──────────────────────────────────────────────────────────────

function ActionBar({
  detail,
  companyId,
}: {
  detail: CertRequestDetail;
  companyId: string;
}) {
  const cr = detail.certRequest!;
  const onbStatus = detail.company.onboardingStatus;
  const updatingProfiles = cr.status === "issued";
  const [rejectOpen, setRejectOpen] = useState(false);
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [testsetOpen, setTestsetOpen] = useState(false);

  const sync = useSyncCertRequest(companyId);
  const approve = useApproveCertRequest(companyId);
  const reject = useRejectCertRequest(companyId);
  const finalize = useFinalizeCertRequest(companyId);
  const runTestset = useRunTestsetid(companyId);
  const syncTenant = useSyncTenant(companyId);
  const markReady = useMarkReady(companyId);

  function onSync() {
    toast.promise(sync.mutateAsync(), {
      loading: "Sincronizando con pahcertificar…",
      success: (res) => `Sincronizado · estado upstream: ${res.pahcert.status}`,
      error: (err) => errorToText(err, "No pudimos sincronizar."),
    });
  }
  function onApprove() {
    toast.promise(approve.mutateAsync(), {
      loading: "Aprobando…",
      success: "Solicitud aprobada (status verified).",
      error: (err) => errorToText(err, "No pudimos aprobar."),
    });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Acciones del operador</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* — submitted: review & verdict — */}
        {cr.status === "submitted" && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={onSync} loading={sync.isPending}>
              <RefreshCw className="h-3.5 w-3.5" /> Sincronizar
            </Button>
            <Button
              variant="primary"
              onClick={onApprove}
              loading={approve.isPending}
            >
              <CheckCircle2 className="h-3.5 w-3.5" /> Aprobar
            </Button>
            <Button variant="danger" onClick={() => setRejectOpen(true)}>
              <FileX className="h-3.5 w-3.5" /> Rechazar
            </Button>
          </div>
        )}

        {/* — verified: ready to pull PFX & finalize — */}
        {cr.status === "verified" && (
          <>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                onClick={onSync}
                loading={sync.isPending}
              >
                <RefreshCw className="h-3.5 w-3.5" /> Sincronizar
              </Button>
              <Button variant="primary" onClick={() => setFinalizeOpen(true)}>
                <ShieldCheck className="h-3.5 w-3.5" /> Finalizar
              </Button>
            </div>
            <p className="text-xs text-slate-500">
              Finalizar trae el PFX + contraseña desde pahcertificar y los
              guarda cifrados en PahFacturar. Necesitas los perfiles de software
              de la DIAN a la mano.
            </p>
          </>
        )}

        {/* — issued: habilitation chain — */}
        {cr.status === "issued" && (
          <div className="space-y-3">
            <HabilitationStage
              onbStatus={onbStatus}
              dianEnv={detail.company.dianEnv}
              sync={{ isPending: sync.isPending, run: onSync }}
              openTestset={() => setTestsetOpen(true)}
              syncTenant={() => {
                toast.promise(syncTenant.mutateAsync(), {
                  loading: "Sincronizando tenant en PROD…",
                  success: "Tenant sincronizado.",
                  error: (err) =>
                    errorToText(err, "No pudimos sincronizar el tenant."),
                });
              }}
              syncTenantPending={syncTenant.isPending}
              markReady={() => {
                toast.promise(markReady.mutateAsync(), {
                  loading: "Marcando como lista…",
                  success: "Empresa marcada lista para facturar.",
                  error: (err) => errorToText(err, "No pudimos marcar lista."),
                });
              }}
              markReadyPending={markReady.isPending}
            />
            <div className="border-t border-slate-200 pt-3">
              <Button variant="outline" onClick={() => setFinalizeOpen(true)}>
                <ShieldCheck className="h-3.5 w-3.5" /> Actualizar perfiles DIAN
              </Button>
              <p className="mt-1 text-[11px] text-slate-500">
                Permite agregar el TestSetId de POS. Los secretos almacenados no
                se muestran: debes ingresar nuevamente los tres perfiles
                completos para reemplazarlos juntos.
              </p>
            </div>
          </div>
        )}

        {/* — rejected — */}
        {cr.status === "rejected" && (
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3">
            <p className="text-sm font-medium text-red-900">
              Solicitud rechazada
            </p>
            <p className="text-xs text-red-700 mt-1">
              Razón: {cr.rejection_reason || "(sin razón registrada)"}
            </p>
            <p className="text-[11px] text-red-700/80 mt-2">
              El usuario debe re-cargar los documentos y reenviar desde la app
              del cliente.
            </p>
          </div>
        )}

        {/* — revoked — */}
        {cr.status === "revoked" && (
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3">
            <p className="text-sm font-medium text-red-900">
              Certificado revocado
            </p>
            <p className="text-xs text-red-700 mt-1">
              {cr.revocation_reason || "Revocado en pahcertificar."}
            </p>
          </div>
        )}

        {/* — draft fallback — */}
        {cr.status === "draft" && (
          <p className="text-sm text-slate-500">
            La solicitud aún está en borrador en el lado del cliente. Esperá a
            que el usuario la envíe.
          </p>
        )}
      </CardContent>

      <RejectDialog
        open={rejectOpen}
        onClose={() => setRejectOpen(false)}
        onConfirm={async (reason) => {
          try {
            await reject.mutateAsync(reason);
            toast.success("Solicitud rechazada.");
            setRejectOpen(false);
          } catch (err) {
            toast.error(errorToText(err, "No pudimos rechazar."));
          }
        }}
        pending={reject.isPending}
      />
      <FinalizeDialog
        open={finalizeOpen}
        onClose={() => setFinalizeOpen(false)}
        nit={detail.company.nit}
        existingCertificate={cr.status === "issued"}
        onConfirm={async (body) => {
          try {
            await finalize.mutateAsync(body);
            toast.success(
              updatingProfiles
                ? "Perfiles DIAN actualizados de forma segura."
                : "Certificado finalizado y guardado de forma segura.",
            );
            setFinalizeOpen(false);
          } catch (err) {
            toast.error(
              errorToText(
                err,
                updatingProfiles
                  ? "No pudimos actualizar los perfiles DIAN."
                  : "No pudimos finalizar.",
              ),
            );
          }
        }}
        pending={finalize.isPending}
      />
      <RunTestsetDialog
        open={testsetOpen}
        onClose={() => setTestsetOpen(false)}
        onConfirm={async (body) => {
          try {
            const res = await runTestset.mutateAsync(body);
            const verdict = res.result?.verdict ?? "pendiente";
            toast.success(`Testset ejecutado · veredicto: ${verdict}`);
            setTestsetOpen(false);
          } catch (err) {
            toast.error(errorToText(err, "No pudimos ejecutar el testset."));
          }
        }}
        pending={runTestset.isPending}
      />
    </Card>
  );
}

function HabilitationStage({
  onbStatus,
  dianEnv,
  sync,
  openTestset,
  syncTenant,
  syncTenantPending,
  markReady,
  markReadyPending,
}: {
  onbStatus: string;
  dianEnv: string;
  sync: { isPending: boolean; run: () => void };
  openTestset: () => void;
  syncTenant: () => void;
  syncTenantPending: boolean;
  markReady: () => void;
  markReadyPending: boolean;
}) {
  // Each deployment talks to one DIAN environment: HAB runs the TestSet;
  // production syncs the tenant's PROD ranges and is then marked ready.
  const prod = dianEnv === "prod";
  return (
    <>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={sync.run} loading={sync.isPending}>
          <RefreshCw className="h-3.5 w-3.5" /> Sincronizar
        </Button>
        {!prod && onbStatus === "cert_uploaded" && (
          <Button variant="primary" onClick={openTestset}>
            <Send className="h-3.5 w-3.5" /> Run testset
          </Button>
        )}
        {prod &&
          (onbStatus === "cert_uploaded" || onbStatus === "hab_passed") && (
            <Button
              variant="primary"
              onClick={syncTenant}
              loading={syncTenantPending}
            >
              <RefreshCw className="h-3.5 w-3.5" /> Sync tenant
            </Button>
          )}
        {prod && onbStatus === "prod_synced" && (
          <Button
            variant="primary"
            onClick={markReady}
            loading={markReadyPending}
          >
            <CheckCircle2 className="h-3.5 w-3.5" /> Marcar lista
          </Button>
        )}
      </div>

      {onbStatus === "ready" && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-start gap-2">
          <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium text-emerald-900">
              Lista para facturar
            </p>
            <p className="text-xs text-emerald-700 mt-0.5">
              La empresa puede emitir documentos en PROD.
            </p>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Dialogs ─────────────────────────────────────────────────────────────────

function RejectDialog({
  open,
  onClose,
  onConfirm,
  pending,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  pending: boolean;
}) {
  const [reason, setReason] = useState("");
  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!pending) {
          setReason("");
          onClose();
        }
      }}
      title="Rechazar solicitud"
      description="El usuario verá esta razón en la app del cliente y podrá reenviar nuevos documentos."
    >
      <div className="space-y-4">
        <div>
          <Label htmlFor="reject-reason" required>
            Razón del rechazo
          </Label>
          <textarea
            id="reject-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={4}
            maxLength={500}
            className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:border-brand-500"
            placeholder="Por ejemplo: el RUT está vencido o ilegible…"
          />
          <p className="text-[11px] text-slate-500 mt-1">{reason.length}/500</p>
        </div>
        <div className="flex justify-end gap-2">
          <Button
            variant="ghost"
            onClick={() => {
              setReason("");
              onClose();
            }}
            disabled={pending}
          >
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={() => onConfirm(reason.trim())}
            loading={pending}
            disabled={!reason.trim() || pending}
          >
            Rechazar
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function FinalizeDialog({
  open,
  onClose,
  nit,
  existingCertificate,
  onConfirm,
  pending,
}: {
  open: boolean;
  onClose: () => void;
  nit: string;
  existingCertificate: boolean;
  onConfirm: (body: FinalizeCertRequestBody) => void;
  pending: boolean;
}) {
  const [body, setBody] = useState<FinalizeCertRequestBody>(() =>
    emptyFinalizeCertRequestBody(nit),
  );

  useEffect(() => {
    if (!open) setBody(emptyFinalizeCertRequestBody(nit));
  }, [nit, open]);

  function update(
    section: "softwareInvoice" | "softwarePos" | "softwarePayroll",
    field: "softwareId" | "softwarePin" | "testSetId",
    value: string,
  ) {
    setBody((prev) => ({
      ...prev,
      [section]: { ...prev[section], [field]: value },
    }));
  }

  const valid = [
    body.softwareInvoice.softwareId,
    body.softwareInvoice.softwarePin,
    body.softwarePos.softwareId,
    body.softwarePos.softwarePin,
    body.softwarePayroll.softwareId,
    body.softwarePayroll.softwarePin,
  ].every((value) => value.trim().length > 0);

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!pending) onClose();
      }}
      title={
        existingCertificate ? "Actualizar perfiles DIAN" : "Finalizar solicitud"
      }
      description={
        existingCertificate
          ? "Reemplaza juntos los perfiles de factura, POS y nómina. Los valores cifrados actuales nunca se recuperan ni se muestran."
          : "Trae el PFX + contraseña desde pahcertificar y los guarda cifrados en PahFacturar junto con los perfiles de software DIAN."
      }
      className="max-w-2xl"
    >
      <div className="space-y-5">
        {(["softwareInvoice", "softwarePos", "softwarePayroll"] as const).map(
          (section) => {
            const labelMap: Record<typeof section, string> = {
              softwareInvoice: "Factura electrónica",
              softwarePos: "POS electrónico",
              softwarePayroll: "Nómina electrónica",
            };
            return (
              <div
                key={section}
                className="border border-slate-200 rounded-md p-3 space-y-3 bg-slate-50"
              >
                <h3 className="text-sm font-semibold text-slate-900">
                  {labelMap[section]}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor={`${section}-id`} required>
                      softwareId
                    </Label>
                    <Input
                      id={`${section}-id`}
                      value={body[section].softwareId}
                      onChange={(e) =>
                        update(section, "softwareId", e.target.value)
                      }
                      className="font-mono"
                      placeholder="UUID DIAN"
                      spellCheck={false}
                    />
                  </div>
                  <div>
                    <Label htmlFor={`${section}-pin`} required>
                      softwarePin
                    </Label>
                    <Input
                      id={`${section}-pin`}
                      type="password"
                      autoComplete="new-password"
                      value={body[section].softwarePin}
                      onChange={(e) =>
                        update(section, "softwarePin", e.target.value)
                      }
                      className="font-mono"
                      placeholder="PIN del portal DIAN"
                      spellCheck={false}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor={`${section}-testset`}>
                      testSetId (opcional)
                    </Label>
                    <Input
                      id={`${section}-testset`}
                      value={body[section].testSetId ?? ""}
                      onChange={(e) =>
                        update(section, "testSetId", e.target.value)
                      }
                      className="font-mono"
                      placeholder={
                        section === "softwarePos"
                          ? "Set de pruebas de documentos equivalentes POS"
                          : "Set de pruebas DIAN"
                      }
                      spellCheck={false}
                    />
                    {section === "softwarePos" ? (
                      <p className="mt-1 text-[11px] text-slate-500">
                        Requerido para emitir una resolución POS de
                        habilitación; no se usa en producción.
                      </p>
                    ) : null}
                  </div>
                </div>
              </div>
            );
          },
        )}
        <div>
          <Label htmlFor="alias">Alias del certificado</Label>
          <Input
            id="alias"
            value={body.alias ?? ""}
            onChange={(e) => setBody((p) => ({ ...p, alias: e.target.value }))}
            className="font-mono"
            placeholder={`pahfacturar-${nit}`}
          />
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
          <Button variant="ghost" onClick={onClose} disabled={pending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={() => onConfirm(normalizeFinalizeCertRequestBody(body))}
            loading={pending}
            disabled={!valid || pending}
          >
            <ShieldCheck className="h-3.5 w-3.5" />
            {existingCertificate
              ? "Guardar perfiles"
              : "Finalizar y guardar certificado"}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function emptyRunTestsetBody(): RunTestsetIdBody {
  return {
    softwareId: "",
    softwarePin: "",
    testSetId: "",
    resolutionNumber: "",
    resolutionDate: "",
    prefix: "",
    fromNumber: 1,
    toNumber: 5_000_000,
    validDateFrom: "",
    validDateTo: "",
    technicalKey: "",
  };
}

function RunTestsetDialog({
  open,
  onClose,
  onConfirm,
  pending,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (body: RunTestsetIdBody) => void;
  pending: boolean;
}) {
  const [body, setBody] = useState<RunTestsetIdBody>(emptyRunTestsetBody);

  useEffect(() => {
    if (!open) setBody(emptyRunTestsetBody());
  }, [open]);

  function set<K extends keyof RunTestsetIdBody>(
    key: K,
    value: RunTestsetIdBody[K],
  ) {
    setBody((p) => ({ ...p, [key]: value }));
  }

  const valid =
    body.softwareId.trim() &&
    body.softwarePin.trim() &&
    body.testSetId.trim() &&
    body.resolutionNumber.trim() &&
    body.resolutionDate.trim() &&
    body.prefix.trim() &&
    body.fromNumber > 0 &&
    body.toNumber > body.fromNumber &&
    body.validDateFrom.trim() &&
    body.validDateTo.trim() &&
    body.technicalKey.trim();

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!pending) onClose();
      }}
      title="Ejecutar testset DIAN HAB"
      description="Envía el bloque de pruebas directamente a DIAN. Si pasa, la empresa entra en estado hab_passed."
      className="max-w-3xl"
    >
      <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="ts-software-id" required>
              softwareId
            </Label>
            <Input
              id="ts-software-id"
              value={body.softwareId}
              onChange={(e) => set("softwareId", e.target.value)}
              className="font-mono"
              spellCheck={false}
            />
          </div>
          <div>
            <Label htmlFor="ts-software-pin" required>
              softwarePin
            </Label>
            <Input
              id="ts-software-pin"
              type="password"
              autoComplete="new-password"
              value={body.softwarePin}
              onChange={(e) => set("softwarePin", e.target.value)}
              className="font-mono"
              spellCheck={false}
            />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="ts-testset" required>
              testSetId
            </Label>
            <Input
              id="ts-testset"
              value={body.testSetId}
              onChange={(e) => set("testSetId", e.target.value)}
              className="font-mono"
              spellCheck={false}
            />
          </div>
        </div>

        <div className="border-t border-slate-200 pt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="ts-res-num" required>
              resolutionNumber
            </Label>
            <Input
              id="ts-res-num"
              value={body.resolutionNumber}
              onChange={(e) => set("resolutionNumber", e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="ts-res-date" required>
              resolutionDate
            </Label>
            <Input
              id="ts-res-date"
              type="date"
              value={body.resolutionDate}
              onChange={(e) => set("resolutionDate", e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="ts-prefix" required>
              prefix
            </Label>
            <Input
              id="ts-prefix"
              value={body.prefix}
              onChange={(e) => set("prefix", e.target.value)}
              className="font-mono uppercase"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label htmlFor="ts-from" required>
                fromNumber
              </Label>
              <Input
                id="ts-from"
                type="number"
                min={1}
                value={body.fromNumber}
                onChange={(e) => set("fromNumber", Number(e.target.value) || 0)}
              />
            </div>
            <div>
              <Label htmlFor="ts-to" required>
                toNumber
              </Label>
              <Input
                id="ts-to"
                type="number"
                min={1}
                value={body.toNumber}
                onChange={(e) => set("toNumber", Number(e.target.value) || 0)}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="ts-valid-from" required>
              validDateFrom
            </Label>
            <Input
              id="ts-valid-from"
              type="date"
              value={body.validDateFrom}
              onChange={(e) => set("validDateFrom", e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="ts-valid-to" required>
              validDateTo
            </Label>
            <Input
              id="ts-valid-to"
              type="date"
              value={body.validDateTo}
              onChange={(e) => set("validDateTo", e.target.value)}
            />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="ts-tech" required>
              technicalKey
            </Label>
            <Input
              id="ts-tech"
              type="password"
              autoComplete="new-password"
              value={body.technicalKey}
              onChange={(e) => set("technicalKey", e.target.value)}
              className="font-mono"
              spellCheck={false}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
          <Button variant="ghost" onClick={onClose} disabled={pending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={() => onConfirm(body)}
            loading={pending}
            disabled={!valid || pending}
          >
            <Send className="h-3.5 w-3.5" /> Ejecutar testset
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

// ─── Audit timeline ──────────────────────────────────────────────────────────

function AuditTimeline({ entries }: { entries: CertRequestAuditEntry[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Auditoría</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 max-h-[640px] overflow-y-auto">
        {entries.length === 0 ? (
          <p className="text-xs text-slate-500">
            Sin acciones registradas todavía.
          </p>
        ) : (
          entries.map((e) => <AuditRow key={e.id} entry={e} />)
        )}
      </CardContent>
    </Card>
  );
}

function AuditRow({ entry }: { entry: CertRequestAuditEntry }) {
  const [open, setOpen] = useState(false);
  const hasDetail = entry.detail !== null && entry.detail !== undefined;
  return (
    <div className="border-l-2 border-slate-200 pl-3 py-1">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs font-medium text-slate-800">
            {ACTION_LABEL[entry.action] ?? entry.action}
          </p>
          <p
            className="text-[11px] text-slate-500 truncate"
            title={entry.actor}
          >
            {entry.actor}
          </p>
        </div>
        <span className="text-[10px] text-slate-400 whitespace-nowrap mt-0.5">
          {formatDate(entry.createdAt)}
        </span>
      </div>
      {(entry.fromStatus || entry.toStatus) && (
        <p className="text-[11px] text-slate-500 mt-1 flex items-center gap-1">
          <span className="font-mono">{entry.fromStatus ?? "—"}</span>
          <ChevronRight className="h-3 w-3" />
          <span className="font-mono">{entry.toStatus ?? "—"}</span>
        </p>
      )}
      {hasDetail && (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="mt-1 inline-flex items-center gap-1 text-[10px] text-slate-500 hover:text-slate-700"
        >
          <ChevronDown
            className={cn("h-3 w-3 transition-transform", open && "rotate-180")}
          />
          {open ? "Ocultar detalle" : "Ver detalle"}
        </button>
      )}
      {open && hasDetail && (
        <pre className="mt-1 text-[10px] font-mono text-slate-700 bg-slate-50 border border-slate-200 rounded p-2 overflow-x-auto">
          {JSON.stringify(entry.detail, null, 2)}
        </pre>
      )}
    </div>
  );
}
